import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionType, FrequencyType } from './AddTransactionFlow';
import { Icon } from '../Icon';
import './FrequencySelector.css';

interface FrequencySelectorProps {
  transactionType: TransactionType;
  value?: FrequencyType;
  isPremium: boolean;
  onChange: (frequency: FrequencyType) => void;
  onBack: () => void;
  recurringOnly?: boolean; // Скрыть опцию "Одноразовая"
}

export const FrequencySelector: React.FC<FrequencySelectorProps> = ({
  transactionType,
  value,
  isPremium,
  onChange,
  onBack,
  recurringOnly = false
}) => {
  const { t } = useTranslation();
  const [showPremiumTeaser, setShowPremiumTeaser] = useState(false);

  // Опции для расходов
  const expenseOptions: Array<{ value: FrequencyType; iconName: string; titleKey: string; descKey: string; premium: boolean }> = [
    { value: 'onetime', iconName: 'check-circle', titleKey: 'transaction.frequency.onetime', descKey: 'transaction.flow.onetimeExpense', premium: false },
    { value: 'weekly', iconName: 'repeat', titleKey: 'transaction.frequency.weekly', descKey: 'transaction.flow.weeklyDesc', premium: true },
    { value: 'monthly', iconName: 'calendar', titleKey: 'transaction.frequency.monthly', descKey: 'transaction.flow.monthlyDesc', premium: true },
    { value: 'yearly', iconName: 'calendar', titleKey: 'transaction.frequency.yearly', descKey: 'transaction.flow.yearlyDesc', premium: true },
  ];

  // Опции для доходов
  const incomeOptions: Array<{ value: FrequencyType; iconName: string; titleKey: string; descKey: string; premium: boolean }> = [
    { value: 'onetime', iconName: 'check-circle', titleKey: 'transaction.frequency.onetime', descKey: 'transaction.flow.onetimeIncome', premium: false },
    { value: 'daily', iconName: 'clock', titleKey: 'transaction.frequency.daily', descKey: 'transaction.flow.dailyDesc', premium: true },
    { value: 'weekly', iconName: 'repeat', titleKey: 'transaction.frequency.weekly', descKey: 'transaction.flow.weeklyDesc', premium: true },
    { value: 'monthly', iconName: 'calendar', titleKey: 'transaction.frequency.monthly', descKey: 'transaction.flow.monthlyDesc', premium: true },
    { value: 'yearly', iconName: 'calendar', titleKey: 'transaction.frequency.yearly', descKey: 'transaction.flow.yearlyDesc', premium: true },
  ];

  let options = transactionType === 'expense' ? expenseOptions : incomeOptions;
  
  // Фильтруем опцию "Одноразовая" если режим только регулярных
  if (recurringOnly) {
    options = options.filter(opt => opt.value !== 'onetime');
  }

  const handleSelect = (frequency: FrequencyType, premium: boolean) => {
    if (premium && !isPremium) {
      setShowPremiumTeaser(true);
      return;
    }
    onChange(frequency);
  };

  return (
    <div className="frequency-selector step-content">
      <div className="frequency-selector__header">
        <button className="frequency-selector__back neo-raised" onClick={onBack}>
          <Icon name="chevron_left" size={20} /> 
        </button>
        <div className="frequency-selector__header-content">
          <h2 className="frequency-selector__title">{t('transaction.flow.frequencyTitle')}</h2>
          <p className="frequency-selector__subtitle">
            {transactionType === 'expense' ? t('transaction.flow.frequencyExpense') : t('transaction.flow.frequencyIncome')}
          </p>
        </div>
      </div>

      <div className="frequency-selector__options">
        {options.map((option) => (
          <button
            key={option.value}
            className={`frequency-option neo-raised ${value === option.value ? 'frequency-option--active' : ''} ${option.premium && !isPremium ? 'frequency-option--locked' : ''}`}
            onClick={() => handleSelect(option.value, option.premium)}
          >
            <div className="frequency-option__icon">
              <Icon name={option.iconName} size={22} color="var(--color-primary)" />
            </div>
            <div className="frequency-option__content">
              <div className="frequency-option__title">
                {t(option.titleKey)}
                {option.premium && !isPremium && <Icon name="star" size={14} color="#FF9500" style={{ marginLeft: '4px' }} />}
              </div>
              <div className="frequency-option__description">{t(option.descKey)}</div>
            </div>
            <div className="frequency-option__check">
              {value === option.value ? <Icon name="check" size={14} color="var(--color-primary)" /> : 
               option.premium && !isPremium ? <Icon name="lock" size={14} color="var(--color-text-secondary)" /> : null}
            </div>
          </button>
        ))}
      </div>

      {/* Premium Teaser Modal */}
      {showPremiumTeaser && (
        <div className="premium-teaser">
          <div className="premium-teaser__overlay" onClick={() => setShowPremiumTeaser(false)} />
          <div className="premium-teaser__content">
            <div className="premium-teaser__icon">
              <Icon name="star" size={56} color="#FF9500" />
            </div>
            <h3 className="premium-teaser__title">{t('transaction.flow.premiumFeature')}</h3>
            <p className="premium-teaser__description">
              {t('transaction.flow.premiumRecurring')}
            </p>
            <div className="premium-teaser__features">
              <div className="premium-feature"><Icon name="check" size={16} color="#34C759" /> {t('transaction.flow.premiumFeature1')}</div>
              <div className="premium-feature"><Icon name="check" size={16} color="#34C759" /> {t('transaction.flow.premiumFeature2')}</div>
              <div className="premium-feature"><Icon name="check" size={16} color="#34C759" /> {t('transaction.flow.premiumFeature3')}</div>
              <div className="premium-feature"><Icon name="check" size={16} color="#34C759" /> {t('transaction.flow.premiumFeature4')}</div>
            </div>
            <button className="premium-teaser__button" onClick={() => setShowPremiumTeaser(false)}>
              {t('common.understood')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

